import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { FileText, Sparkles, Download, Plus, Leaf, ShieldCheck, Users, Send, CheckCircle2, ExternalLink } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContratsUniverselsPage() {
  const [contrats, setContrats] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [selectedSociete, setSelectedSociete] = useState('');
  const [selected, setSelected] = useState(null);

  // Génération IA
  const [iaForm, setIaForm] = useState({
    type_contrat: 'bipartite',
    description: '',
    inclure_annexe_bio: false,
  });
  const [generating, setGenerating] = useState(false);

  const loadContrats = useCallback(async () => {
    if (!selectedSociete) return;
    try {
      const r = await api.get(`/contrats-universels/?societe_id=${selectedSociete}`);
      setContrats(r.data?.contrats || []);
    } catch (e) { console.error(e); }
  }, [selectedSociete]);

  useEffect(() => {
    (async () => {
      try {
        let items = [];
        try {
          const r = await api.get('/google/all-societes-status');
          items = r.data?.societes || [];
        } catch { /* ignore */ }
        if (!items.length) {
          const r2 = await api.get('/tenant/societes');
          items = r2.data?.societes || r2.data || [];
        }
        setSocietes(items);
        if (items.length && !selectedSociete) setSelectedSociete(items[0].id || items[0].societe_id);
      } catch (e) { console.error(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadContrats(); }, [loadContrats]);

  const handleGenererIA = async () => {
    if (!selectedSociete || !iaForm.description.trim()) {
      toast.error('Décris ton contrat en texte libre');
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post('/contrats-universels/generer-ia', {
        societe_id: selectedSociete,
        type_contrat: iaForm.type_contrat,
        description: iaForm.description,
        inclure_annexe_bio: iaForm.inclure_annexe_bio,
      });
      if (r.data.success) {
        toast.success('Contrat généré par IA ✨');
        setSelected(r.data.contrat);
        setIaForm({ ...iaForm, description: '' });
        await loadContrats();
      }
    } catch (e) {
      toast.error('Erreur IA: ' + (e.response?.data?.detail || e.message));
    } finally { setGenerating(false); }
  };

  const handleDownloadPDF = async (contratId) => {
    try {
      const token = localStorage.getItem('token');
      const url = `${BACKEND_URL}/api/contrats-universels/${contratId}/pdf`;
      const resp = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `contrat_${contratId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      toast.success('PDF téléchargé');
    } catch (e) {
      toast.error('Erreur téléchargement: ' + e.message);
    }
  };

  const handleEnvoyerSignature = async (contrat) => {
    const email = window.prompt(
      `Envoyer le contrat pour signature électronique.\n\nEmail du destinataire :`,
      ''
    );
    if (!email || !email.includes('@')) {
      if (email) toast.error('Email invalide');
      return;
    }
    const message = window.prompt('Message personnalisé (optionnel) :', '') || '';
    try {
      const r = await api.post(`/contrats-universels/${contrat.id}/envoyer-signature`, {
        email_destinataire: email,
        message_personnalise: message,
      });
      if (r.data.success) {
        toast.success('Email envoyé au client avec le lien de signature ✅');
        await loadContrats();
      } else {
        toast.error('Envoi échoué');
      }
    } catch (e) {
      toast.error('Erreur: ' + (e.response?.data?.detail || e.message));
    }
  };

  const examplesTripartite = `Contrat tripartite pour 17 ha de menthe poivrée entre SCEA Domaine des Arômes (producteur, Fred Rancelli), Formulance SAS (intermédiaire, Samuel Ducros, 13 rue des Émeraudes 69006 Lyon) et Reynaud et Fils (acheteur, Saint-Didier). Prix producteur vers intermédiaire 80€/kg HT, prix intermédiaire vers acheteur 90€/kg HT. Durée 5 ans à partir du 01/01/2026. Rendement prévisionnel 65 kg/ha/an à partir de 2027. Paiement virement 30 jours, acompte 20% signature.`;

  const examplesBipartite = `Contrat entre ETA Sauvayre (prestataire, Laurent Sauvayre) et Domaine Bour (client, Roussas 26230) pour la campagne viticole 2026. Prestations : Traitement phyto 55€/ha (phyto fournis par client), Vendange mécanique 280€/ha (benne comprise), Effeuillage mécanique 190€/ha, Labour 75€/ha. Surface totale 25 ha en viticulture conventionnelle. Facturation mensuelle au réel, virement 30 jours net.`;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="contrats-universels-page">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-emerald-900 flex items-center gap-2">
            <FileText className="w-7 h-7" /> Contrats Universels
          </h1>
          <p className="text-slate-600 mt-1">
            Bipartites ou tripartites, multi-cultures, multi-prestations. Annexe BIO et clauses
            sécurité/EPI/ZNT/DAR intégrées. Génération IA depuis description libre.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Génération IA */}
        <Card className="lg:col-span-2 border-purple-200">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
            <CardTitle className="flex items-center gap-2 text-purple-900">
              <Sparkles className="w-5 h-5" /> Générer un contrat avec IA
            </CardTitle>
            <p className="text-xs text-purple-700 mt-1">
              Décris ton besoin en français — l'IA structure tout (parties, prix, durée, clauses)
            </p>
          </CardHeader>
          <CardContent className="space-y-3 pt-4">
            <div>
              <label className="text-xs font-semibold text-slate-600">Société</label>
              <select
                data-testid="societe-select-u"
                value={selectedSociete}
                onChange={e => setSelectedSociete(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded"
              >
                {societes.map(s => (
                  <option key={s.id || s.societe_id} value={s.id || s.societe_id}>
                    {s.nom || s.societe_nom}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Type de contrat</label>
                <select
                  value={iaForm.type_contrat}
                  onChange={e => setIaForm({ ...iaForm, type_contrat: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border rounded"
                  data-testid="type-contrat-select"
                >
                  <option value="bipartite">Bipartite (prestataire ↔ client)</option>
                  <option value="tripartite">Tripartite (producteur ↔ intermédiaire ↔ acheteur)</option>
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm cursor-pointer p-2 bg-emerald-50 rounded w-full">
                  <input
                    type="checkbox"
                    checked={iaForm.inclure_annexe_bio}
                    onChange={e => setIaForm({ ...iaForm, inclure_annexe_bio: e.target.checked })}
                    className="w-4 h-4"
                    data-testid="annexe-bio-checkbox"
                  />
                  <Leaf className="w-4 h-4 text-emerald-700" />
                  <span>Annexe BIO (CDC AB, lavage outils, EPI)</span>
                </label>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-600">Description du contrat</label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setIaForm({ ...iaForm, description: examplesBipartite, type_contrat: 'bipartite' })}
                    className="text-xs text-purple-700 hover:underline"
                  >Exemple Bipartite</button>
                  <span className="text-slate-300">·</span>
                  <button
                    type="button"
                    onClick={() => setIaForm({ ...iaForm, description: examplesTripartite, type_contrat: 'tripartite' })}
                    className="text-xs text-purple-700 hover:underline"
                  >Exemple Tripartite</button>
                </div>
              </div>
              <Textarea
                rows={6}
                data-testid="description-textarea"
                value={iaForm.description}
                onChange={e => setIaForm({ ...iaForm, description: e.target.value })}
                placeholder="Ex: Contrat entre ETA Sauvayre et Domaine Bour pour la campagne 2026. Traitement phyto 55€/ha, Vendange méca 280€/ha, surface 25 ha..."
                className="mt-1 text-sm"
              />
            </div>

            <Button
              data-testid="generer-ia-btn"
              onClick={handleGenererIA}
              disabled={generating || !iaForm.description.trim()}
              className="w-full bg-gradient-to-r from-purple-700 to-pink-600 hover:from-purple-800 hover:to-pink-700 text-white"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {generating ? 'L\'IA rédige ton contrat...' : 'Générer le contrat'}
            </Button>
          </CardContent>
        </Card>

        {/* Stats / résumé */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Vue d'ensemble</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="p-3 bg-emerald-50 rounded">
              <div className="text-xs text-emerald-700">Contrats en base</div>
              <div className="text-2xl font-bold text-emerald-900">{contrats.length}</div>
            </div>
            <div className="p-3 bg-purple-50 rounded">
              <div className="text-xs text-purple-700">Tripartites</div>
              <div className="text-2xl font-bold text-purple-900">
                {contrats.filter(c => c.type_contrat === 'tripartite').length}
              </div>
            </div>
            <div className="p-3 bg-amber-50 rounded">
              <div className="text-xs text-amber-700">Avec annexe BIO</div>
              <div className="text-2xl font-bold text-amber-900">
                {contrats.filter(c => c.inclure_annexe_bio).length}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Liste + détail */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4" /> Contrats ({contrats.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[500px] overflow-auto">
            {contrats.length === 0 && (
              <p className="text-sm text-slate-500">Aucun contrat. Génère le premier avec l'IA.</p>
            )}
            {contrats.map(c => (
              <div
                key={c.id}
                onClick={() => setSelected(c)}
                className={`p-3 border rounded cursor-pointer hover:bg-slate-50 ${selected?.id === c.id ? 'border-emerald-500 bg-emerald-50' : ''}`}
                data-testid={`contrat-u-item-${c.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-sm truncate flex-1">{c.titre}</div>
                  <Badge className={c.type_contrat === 'tripartite' ? 'bg-purple-100 text-purple-800' : 'bg-emerald-100 text-emerald-800'}>
                    {c.type_contrat}
                  </Badge>
                </div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                  <span>{(c.parties || []).length} parties</span>
                  <span>·</span>
                  <span>{(c.cultures || []).length} culture(s)</span>
                  <span>·</span>
                  <span>{(c.prestations || []).length + (c.prix_vente || []).length} lignes prix</span>
                  {c.inclure_annexe_bio && <Leaf className="w-3 h-3 text-emerald-600" />}
                  {c.generated_by_ai && <Sparkles className="w-3 h-3 text-purple-600" />}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Détail */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between gap-2">
              <span className="truncate">{selected ? selected.titre : 'Détail'}</span>
              {selected && (
                <div className="flex gap-1 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownloadPDF(selected.id)}
                    data-testid="download-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-1" /> PDF
                  </Button>
                  {selected.statut !== 'signe' && (
                    <Button
                      size="sm"
                      onClick={() => handleEnvoyerSignature(selected)}
                      data-testid="envoyer-signature-btn"
                      className="bg-emerald-700 hover:bg-emerald-800"
                    >
                      <Send className="w-4 h-4 mr-1" /> Envoyer signature
                    </Button>
                  )}
                  {selected.statut === 'signe' && (
                    <Button size="sm" variant="outline" disabled className="text-emerald-700 border-emerald-300">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Signé
                    </Button>
                  )}
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            {!selected && <p className="text-slate-500">Sélectionne un contrat à gauche.</p>}
            {selected && (
              <>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">Début:</span> {selected.date_debut}</div>
                  <div><span className="text-slate-500">Fin:</span> {selected.date_fin}</div>
                  <div><span className="text-slate-500">Durée:</span> {selected.duree_annees || 1} an(s)</div>
                  <div><span className="text-slate-500">Lieu:</span> {selected.lieu_execution || '—'}</div>
                </div>

                <div>
                  <div className="font-semibold text-xs mb-1 flex items-center gap-1"><Users className="w-3 h-3" /> Parties</div>
                  {(selected.parties || []).map((p, i) => (
                    <div key={i} className="p-2 bg-slate-50 rounded mb-1 text-xs">
                      <span className="uppercase text-slate-500 text-[10px] mr-2">{p.role}</span>
                      <span className="font-semibold">{p.nom}</span>
                      {p.representant && <span className="text-slate-500"> — {p.representant}</span>}
                    </div>
                  ))}
                </div>

                {selected.cultures?.length > 0 && (
                  <div>
                    <div className="font-semibold text-xs mb-1">Cultures</div>
                    {selected.cultures.map((c, i) => (
                      <div key={i} className="flex justify-between text-xs border-b py-1">
                        <span>{c.nom} {c.variete && `(${c.variete})`}</span>
                        <span>{c.surface_ha || 0} ha {c.bio && <Leaf className="w-3 h-3 inline text-emerald-700 ml-1" />}</span>
                      </div>
                    ))}
                  </div>
                )}

                {selected.prestations?.length > 0 && (
                  <div>
                    <div className="font-semibold text-xs mb-1">Prestations & tarifs</div>
                    <table className="w-full text-xs">
                      <thead><tr className="text-slate-500 text-left"><th>Type</th><th>Prix</th><th>Unité</th></tr></thead>
                      <tbody>
                        {selected.prestations.map((p, i) => (
                          <tr key={i} className="border-t">
                            <td className="py-1">{p.type}</td>
                            <td className="font-bold">{p.prix_ht} €</td>
                            <td>{p.unite}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {selected.prix_vente?.length > 0 && (
                  <div>
                    <div className="font-semibold text-xs mb-1">Prix de vente</div>
                    {selected.prix_vente.map((pv, i) => (
                      <div key={i} className="flex justify-between text-xs border-b py-1">
                        <span>{pv.de} → {pv.vers}</span>
                        <span className="font-bold">{pv.montant_ht} €/{pv.unite}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-wrap gap-2 text-xs pt-2 border-t">
                  {selected.inclure_annexe_bio && <Badge className="bg-emerald-100 text-emerald-800"><Leaf className="w-3 h-3 mr-1" />Annexe BIO</Badge>}
                  {selected.inclure_clauses_securite_epi && <Badge className="bg-blue-100 text-blue-800"><ShieldCheck className="w-3 h-3 mr-1" />Sécurité/EPI</Badge>}
                  {selected.inclure_clause_force_majeure && <Badge variant="outline">Force majeure</Badge>}
                  {selected.inclure_clause_confidentialite && <Badge variant="outline">Confidentialité</Badge>}
                  {selected.clauses_personnalisees?.length > 0 && (
                    <Badge variant="outline">+{selected.clauses_personnalisees.length} clause(s) perso</Badge>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
